class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.writeItemAddedAnnouncement();
      },
      { once: true }
    );

    this.setBackgroundInert(true);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    // Inert has to come off before focus is restored, or the element we are
    // handing focus back to is still unfocusable.
    this.setBackgroundInert(false);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Clear the announcement so it is not left sitting in the accessibility
    // tree as stale text the next time the drawer is browsed.
    const liveRegion = this.querySelector('#CartDrawer-AddedLiveRegion');
    if (liveRegion) liveRegion.textContent = '';
  }

  // The drawer is aria-modal, but trapFocus only constrains the tab order. A
  // screen reader's virtual cursor ignores focus traps, so without inert a
  // screen reader user can read straight out of the "modal" into the page
  // behind it. Settlement complaint para 38(a).
  setBackgroundInert(isInert) {
    const IGNORED_TAGS = ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'];

    Array.from(document.body.children).forEach((element) => {
      if (element === this || element.contains(this)) return;
      if (IGNORED_TAGS.includes(element.tagName)) return;

      if (isInert) {
        // Leave alone anything another component already inerted, so we do not
        // clear its state when we close.
        if (element.hasAttribute('inert')) return;
        element.setAttribute('inert', '');
        element.setAttribute('data-cart-drawer-inert', '');
      } else if (element.hasAttribute('data-cart-drawer-inert')) {
        element.removeAttribute('inert');
        element.removeAttribute('data-cart-drawer-inert');
      }
    });
  }

  // Settlement complaint para 38(a): adding an item has to be announced to
  // screen reader users, not just shown visually. This only flags the
  // announcement; the text is written once focus is inside the dialog.
  announceItemAdded() {
    this.pendingItemAdded = true;
    // Already open (checked before open()'s deferred class change lands): the
    // drawer will not transition again, so there is no transitionend to wait for.
    if (this.classList.contains('active')) this.writeItemAddedAnnouncement();
  }

  // Runs from open()'s transitionend handler, after trapFocus. The region is a
  // role="alert", which interrupts, so the delay lets the drawer's own "Your
  // cart, dialog" focus announcement finish first. The alert only reached
  // VoiceOver once focus was inside the dialog (tested by ear 09/14).
  writeItemAddedAnnouncement() {
    if (!this.pendingItemAdded) return;
    this.pendingItemAdded = false;

    const liveRegion = this.querySelector('#CartDrawer-AddedLiveRegion');
    if (!liveRegion || !window.cartStrings) return;

    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = window.cartStrings.itemAdded;
    }, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.announceItemAdded();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
